import { router, useForm } from "@inertiajs/react";
import { useCallback, useEffect, useRef } from "react";

function getFiltersFromURL(defaultFilters) {
  const params = new URLSearchParams(window.location.search);
  const filtersFromURL = {};

  Object.keys(defaultFilters).forEach((key) => {
    if (params.has(key)) {
      filtersFromURL[key] = params.get(key);
    }
  });

  return { ...defaultFilters, ...filtersFromURL };
}

const useDebounce = () => {
  const timeout = useRef();

  const debounce =
    (func, wait) =>
    (...args) => {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => func(...args), wait);
    };

  useEffect(() => {
    return () => {
      if (!timeout.current) return;
      clearTimeout(timeout.current);
    };
  }, []);

  return { debounce };
};

export function useFilters({ defaultFilters, routerOptions = {} }) {
  const initialFilters = getFiltersFromURL(defaultFilters);
  const transform = useRef((data) => data);
  const { debounce } = useDebounce();

  const { data, setData, isDirty, ...rest } = useForm(initialFilters);

  const debouncedUpdateFilters = debounce((newData) => {
    router.get(window.location.pathname, newData, {
      preserveState: true,
      preserveScroll: true,
      preserveUrl: true,
      ...routerOptions,
    });
  }, 500);

  const updateFilter = (key, value, options = { debounce: false }) => {
    const newData = { ...data, [key]: value };
    setData(key, value);
    if (options.debounce) {
      debouncedUpdateFilters(transform.current(newData));
    } else {
      router.get(window.location.pathname, transform.current(newData), {
        preserveState: true,
        preserveScroll: true,
        preserveUrl: true,
        ...routerOptions,
      });
    }
  };

  const resetFilters = () => {
    setData(defaultFilters);
    router.get(window.location.pathname, defaultFilters, {
      preserveState: true,
      preserveScroll: true,
      ...routerOptions,
    });
  };

  const transformFunction = useCallback((callback) => {
    transform.current = callback;
  }, []);

  return { data, updateFilter, resetFilters, isDirty, ...rest, transform: transformFunction };
}
